<template>
  <section
    class="container d-flex flex-column p-0 px-3"
    style="min-height: 100%"
    data-testid="news"
  >
    <BootstrapNav
      id="news.nav"
      :nav="navItems"
      :active="activeNavItem"
      variant="underline"
      class="top-0"
      all
      @update:active="(n: BootstrapNavItem | undefined) => (activeNavItem = n)"
    />
    <div class="d-flex flex-grow-1 w-100 mt-3">
      <NewsItem v-if="selectedNewsItemId" :news-page-id="selectedNewsItemId" :editor="isEditor" />
      <NewsFeed
        :active="selectedNewsItemId"
        :editor="isEditor"
        :loading="loading"
        class="ms-2 w-25"
        @update-item="handleNewsItemClick"
        @update-cursor="handlePageChange"
      />
    </div>
  </section>
</template>
<script setup lang="ts">
import {type BootstrapNavItem} from '@/components/shared/bootstrap/BootstrapNav/BootstrapNav.types';

const route = useRoute();
const router = useRouter();

const userSettingsStore = useUserSettingsStore();
const userStore = useUserStore();
const newsStore = useNewsStore();

const language = computed(() => userSettingsStore.userSettings.language);

const isEditor = computed(() => userStore.isCmsEditor);

// Nav
const newsItems = computed<NewsItem[]>(() => newsStore.newsItems);
const newsIndexList = computed(() => newsStore.newsIndexList);
const navItems = computed<BootstrapNavItem[]>(() => [
  ...newsIndexList.value.map(
    (i): BootstrapNavItem => ({id: String(i.meta.id), title: i.meta.title}),
  ),
]);
const activeNavItem = ref<BootstrapNavItem>();
const selectedNewsItemId = ref<number | null>(route.params.id ? Number(route.params.id) : null);

const loading = ref(true);

const handlePageChange = async (newPage: number) => {
  loading.value = true;
  newsStore.offset = (newPage - 1) * newsStore.limit;
  try {
    await newsStore.loadRecentNews(Number(activeNavItem.value?.id));
    loading.value = false;
  } catch {
    // Error handled silently
  }
};

const handleNewsItemClick = async (newsItem: NewsItem | undefined) => {
  if (!newsItem) return;
  selectedNewsItemId.value = newsItem.meta.id;
  router.replace({params: {id: newsItem.meta.id}});
  try {
    await newsStore.getNewsItem(newsItem.meta.id);
  } catch (error) {
    console.error((error as Error).message);
  }
};

watch(language, async () => {
  try {
    loading.value = true;
    activeNavItem.value = undefined;
    newsStore.offset = 0;
    await newsStore.loadRecentNews();
    await newsStore.getNewsIndexList();
    loading.value = false;
  } catch (error) {
    console.error((error as Error).message);
  }
});

watch(
  () => route,
  async (newRoute) => {
    try {
      loading.value = true;
      if (newRoute.name !== 'news') {
        activeNavItem.value = undefined;
        await newsStore.loadRecentNews();
      }
      if (Number(newRoute.params.id) !== selectedNewsItemId.value && newRoute.params.id) {
        selectedNewsItemId.value = Number(newRoute.params.id);
      } else if (!selectedNewsItemId.value) {
        handleNewsItemClick(newsItems.value[0]);
      }
      loading.value = false;
    } catch (error) {
      console.error((error as Error).message);
    }
  },
  {deep: true},
);

watch(activeNavItem, async (newNavItem) => {
  try {
    loading.value = true;
    newsStore.offset = 0;
    const id = newNavItem?.id ? Number(newNavItem?.id) : null;
    await newsStore.loadRecentNews(id);
    loading.value = false;
  } catch (error) {
    console.error((error as Error).message);
  }
});

watch(
  () => route.query.source,
  (sourceId) => {
    if (sourceId) {
      const n = navItems.value.find((n) => n.id === sourceId);
      activeNavItem.value = n;
    }
  },
);

onMounted(async () => {
  try {
    loading.value = true;

    const sourceId = route.query.source ? route.query.source : null;
    if (sourceId) {
      const n = navItems.value.find((n) => n.id === sourceId);
      activeNavItem.value = n;
    }

    const id = activeNavItem.value?.id ? Number(activeNavItem.value.id) : null;
    await newsStore.loadRecentNews(id);
    await newsStore.getNewsIndexList();
    if (newsItems.value.length > 0 && !selectedNewsItemId.value) {
      selectedNewsItemId.value = newsItems.value[0].meta.id;
    }
    loading.value = false;
  } catch (error) {
    console.error((error as Error).message);
  }
});
</script>
